import {Component, OnDestroy, OnInit} from '@angular/core';
import {AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {RouterExtService} from '@Services/base/router/router.service';
import {Individu, IndividuService, PubSubService} from '@Common/core/services';
import {LoaningService} from '../../common/services/loaning/loaning.service';
import {Loaning} from '../../common/services/loaning/loaning.model';
import {LoaningState} from '../../common/services/loaning/loaning-states.enum';
import {DialogData, DialogValidationComponent} from '@Common/widgets/general/dialog-validation';
import {MatDialog} from '@angular/material/dialog';
import {DirCmpEntite} from '../../common/services/dircmp/dircmp.model';
import {DirCmpArchiveService} from '../../common/services/dircmp/dircmp.service';
import {SubSink} from '@Common/core/utils/subsink';
import {catchError, finalize} from 'rxjs/operators';
import {SnackBarToastyService, typeToast} from '@Common/widgets/general/snack-bar';
import {throwError} from 'rxjs';
import {FieldsConsultHelper} from './fields-consult.helper';

@Component({
  selector: 'app-loaning-edit',
  templateUrl: './loaning-edit.component.html',
  styleUrls: ['./loaning-edit.component.scss']
})
export class LoaningEditComponent extends FieldsConsultHelper implements OnInit, OnDestroy  {
  consultationForm: UntypedFormGroup;
  selectedTabIndex = 0;
  currentConsult: Loaning = {id: 0, status: LoaningState.DRAFT} as Loaning;
  loaningState = LoaningState;
  dirCmpArchivage: DirCmpEntite[] = [];
  connecte: Individu;
  loading = false;
  subSink = new SubSink();
  private _previousUrl;
  constructor(private router: Router,
              private routerExtSvc: RouterExtService,
              private route: ActivatedRoute,
              public dialog: MatDialog,
              private pubSubSvc: PubSubService,
              private individuSvc: IndividuService,
              private snackSvc: SnackBarToastyService,
              private formBuilder: UntypedFormBuilder,
              private dirCmpSvc: DirCmpArchiveService,
              private loaningSvc: LoaningService) {
    super();
    this._initConsultationForm();
  }
  ngOnInit(): void {
    this._getConnectedAndConsult();
    this._fetchDirCmp();
    this._previousUrl = this.routerExtSvc.getPreviousUrl();
  }
  public get dirCmp(): number {
    return this.consultationForm?.get(this.FIELD_DIR_CMP)?.value;
  }
  private _getConnectedAndConsult() {
    this.subSink.sink = this.individuSvc.individu$
      .subscribe((_individu) => {
        this.connecte = _individu;
        this._initOrGetConsult();
      });
  }
  formControlProperty(name: string) {
    return this.consultationForm.get(name);
  }
  formMaxLengthProperty(name: string) {
    return this.maxLengthProperties.get(name) || 20;
  }
  onSelectDirCmp() {
    const dirCmp = this.dirCmp;
    if (dirCmp) {
      this.consultationForm.patchValue({[this.FIELD_DIR_CMP]: this.dirCmp});
      this.currentConsult.dir_cmp = this.dirCmp;
    }
  }
  canSendDraft() {
    return this.currentConsult.status === LoaningState.DRAFT;
  }
  saveConsultationForm(state: LoaningState) {
    this._mapFormToModel();
    this.pubSubSvc.publish('loading', true);
    this.currentConsult.status = state;
    this.subSink.sink = this.loaningSvc
      .updateOrcreate(this.currentConsult, 'uuid')
      .pipe(finalize((() => this.pubSubSvc.publish('loading', false))))
      .subscribe(
        _data => {
          const message = this.currentConsult.status === LoaningState.DRAFT ?
            'La demande de consultation a bien été enregistré en mode brouillon, elle n\'a pas été envoyée' :
            'La demande de consultation a bien été envoyée pour traitement';
          this.snackSvc.toasty('Demande de consultation', message, typeToast.success);
          this.currentConsult = _data;
          if (this.currentConsult.status === LoaningState.WAITING) {
            this.backToPreviousUrl();
          }
        });
  }
  private _mapFormToModel() {
    const _getProperty = (_key) => Object.keys(_key)[0];
    this.fieldsForm.forEach((property) => {
      const _property = _getProperty(property);
      let _value = this.formControlProperty(_getProperty(property)).value;
      // on force à null pour éviter une chaine de car. "" pour les années, lors d'une nouvelle consultation
      if ((_property === this.FIELD_YEAR_MIN || _property === this.FIELD_YEAR_MAX) && _value === '') {
        _value = null;
      }
      this.currentConsult[_getProperty(property)] = _value;
    });
  }
  /**
   * Init des valeurs de la consultation trouvée vers les forms controls group consultationForm
   * @param {Loaning} loaning
   * @private
   */
  private _consultToForm(loaning: Loaning) {
    this.onSelectDirCmp();
    this.consultationForm.patchValue({
      [this.FIELD_DIR_CMP]: loaning?.dir_cmp,
      [this.FIELD_SVC]: loaning.svc,
      [this.FIELD_OFFICE]: loaning.office,
      [this.FIELD_PHONENUMBER]: loaning.phone_number,
      [this.FIELD_FIRST_NAME]: loaning.first_name,
      [this.FIELD_LAST_NAME]: loaning.last_name,
      [this.FIELD_YEAR_MIN]: loaning.year_min,
      [this.FIELD_YEAR_MAX]: loaning.year_max,
      [this.FIELD_TYPOLOGIE]: loaning.typologie,
      [this.FIELD_DETAILS]: loaning.details,
    });
  }
  sendLoaning() {
    const data = new DialogData();
    data.title = `Confirmation de l'envoi de la demande de consultation`;
    data.message = '';
    const dialogRef = this.dialog.open(DialogValidationComponent, {data: data});
    dialogRef.updatePosition({top: '50px'});
    dialogRef.afterClosed().subscribe(
      result => {
        if (result) {
          this.saveConsultationForm(LoaningState.WAITING);
        }
      }
    );
  }
  canSendLoaning() {
    if (! (this.currentConsult.status === LoaningState.DRAFT)) {
      return false;
    }
    return this._checkMandatoryFieldsToSend();
  }
  toolTipSendButton() {
    if (this.canSendLoaning()) {
      return 'Envoyer votre demande de consultation';
    }
    return 'Veuillez remplir tous les champs obligatoires indiqués par *, les dates doivent être aussi respectées, afin de pouvoir envoyer votre demande de consultation';
  }
  getTitle() {
    if (this._isNew()) {
      return 'Nouvelle demande de consultation';
    }
    return 'Modification d\'une demande de consultation';
  }
  backToPreviousUrl(backTo= '/consultation') {
    if (this._previousUrl) {
      this.router.navigate([this._previousUrl]);
    } else {
      this.router.navigate([backTo]);
    }
  }
  /**
   * Test la validité des dates pour l'envoi de la demande
   */
  private _isValidYears() {
    const _errorsYearMin = this.consultationForm?.controls[this.FIELD_YEAR_MIN]?.errors;
    const _errorsYearMax = this.consultationForm?.controls[this.FIELD_YEAR_MAX]?.errors;
    const _errors = this.consultationForm?.errors;
    return !(
      _errors?.yearRangeInvalid ||
      _errorsYearMin?.invalidYear ||
      _errorsYearMax?.invalidYear
    );
  }
  /**
   * Test si les champs obligatoires pour l'envoi sont bien remplis
   */
  private _isValidFields() {
    return this.fieldsForm.every((field: { [p: string]: boolean }) => {
      const [key, isMandatory] = Object.entries(field)[0];
      const value = this.formControlProperty(key).value;
      return !isMandatory || !!value;
    });
  }
  private _checkMandatoryFieldsToSend = () => this._isValidYears() && this._isValidFields();
  private _isNew() {
    return !this.currentConsult || !this.currentConsult?.id;
  }
  /**
   * Init consultation si chargement pour modification
   * @private
   */
  private _initOrGetConsult() {
    const id = this.route.snapshot.params['id'];
    console.log(`init or get consult ${id}`);
    if (id) {
      this.loading = true;
      this.pubSubSvc.publish('loading', this.loading);
      this.subSink.sink = this.loaningSvc.get(id)
        .pipe(
          catchError((error) => {
            this.snackSvc.toasty('Modification d\'une demande de consultation',
                                 'Vous n\'avez pas accès à cette demande pour modification', typeToast.error);
            this.backToPreviousUrl();
            return throwError(error);
          }),
          finalize(() => {
            this.loading = false;
            this.pubSubSvc.publish('loading', false);
          }))
        .subscribe((_loaning: Loaning) => {
          this.currentConsult = _loaning;
          this._consultToForm(_loaning);
        });
    } else {
      this.currentConsult = {id: 0, borrower: this.connecte.uid, status: LoaningState.DRAFT} as Loaning;
    }
  }
  /**
   * init formulaire demande de consultation
   */
  private _initConsultationForm() {
    this.consultationForm = this.formBuilder.group({
      [this.FIELD_DIR_CMP]: [''],
      [this.FIELD_SVC]: ['', [Validators.maxLength(this.formMaxLengthProperty(this.FIELD_SVC))]],
      [this.FIELD_FIRST_NAME]: ['', [Validators.maxLength(this.formMaxLengthProperty(this.FIELD_FIRST_NAME))]],
      [this.FIELD_LAST_NAME]: ['', [Validators.maxLength(this.formMaxLengthProperty(this.FIELD_LAST_NAME))]],
      [this.FIELD_OFFICE]: ['', [Validators.maxLength(this.formMaxLengthProperty(this.FIELD_PHONENUMBER))]],
      [this.FIELD_YEAR_MIN]: ['', [this.yearValidator()]],
      [this.FIELD_YEAR_MAX]: ['', [this.yearValidator()]],
      [this.FIELD_TYPOLOGIE]: ['', [Validators.maxLength(this.formMaxLengthProperty(this.FIELD_TYPOLOGIE))]],
      [this.FIELD_DETAILS]: [''],
      [this.FIELD_PHONENUMBER]: ['', [Validators.pattern('^(?:(?:\\+|00)33|0)\\s*[1-9](?:[\\s.-]*\\d{2}){4}$')]],
    }, {
      validators: this.yearRangeValidator()
    });
  }
  private _fetchDirCmp() {
    this.subSink.sink = this.dirCmpSvc
      .listAllItems()
      .subscribe((_data) => {
        this.dirCmpArchivage = _data.list;
        this.dirCmpSvc.sortBy(this.dirCmpArchivage);
      });
  }
  /**
   * Validators années
   */
  yearValidator(): ValidatorFn {
    /**
     * validation du format de l'année entre 1964 et 2100
     * l'année ne peut dépasser l'année courante
     */
    return (control: AbstractControl): ValidationErrors | null => {
      const currentYear = new Date().getFullYear();
      const year = control.value;
      const isWithinRange = year >= 1964 && year <= Math.min(currentYear, 2100);
      if (year && !isWithinRange) {
        return { invalidYear: true };
      }
      return null; // année valide
    };
  }
  yearRangeValidator(): ValidatorFn {
    /**
     * year_max doit être > à year_min
     */
    return (group: AbstractControl): ValidationErrors | null => {
      const yearMin = group.get(this.FIELD_YEAR_MIN)?.value;
      const yearMax = group.get(this.FIELD_YEAR_MAX)?.value;
      if (yearMin && yearMax && yearMin > yearMax) {
        return { yearRangeInvalid: true };
      }
      return null; // range valide
    };
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }
}



